import { getAzureBlob, updateOutputSurface } from "../../../services/container";
import { getOutputSurfaceBlobNames } from "../../../shared/utils/utils";

const buildParams = (baseParams, objectSegmentations) => {
  const anomaliesCount = objectSegmentations.filter(
    (item) => !item.isAuthorizedMaterial,
  ).length;
  return {
    ...baseParams,
    object_segmentations: objectSegmentations,
    anomalies: anomaliesCount,
  };
};

/**
 * Remap output-surface images for a history cycle after a waste-category (flux)
 * change: update authorized materials + isAuthorizedMaterial on segmentations.
 * Does not touch container.contentCategory.
 */
export async function applyWasteCategoryToCycleSurfaces({
  wasteCategoryJson,
  characterization = false,
  containerId,
  historyEntries = [],
  azureUrl,
}) {
  if (
    !containerId ||
    !Array.isArray(historyEntries) ||
    historyEntries.length === 0
  ) {
    return { updatedCount: 0 };
  }

  const wasteCategory = wasteCategoryJson
    ? JSON.parse(wasteCategoryJson)
    : null;
  let updatedCount = 0;

  await Promise.all(
    historyEntries.map(async (history) => {
      let objectSegmentations = [];
      let baseParams = {
        containerId,
        time: history._time,
        characterization,
      };

      if (wasteCategory) {
        baseParams = {
          ...baseParams,
          waste_category_label: wasteCategory.wasteCategoryName,
          waste_category_value: wasteCategory.wasteCategoryValue,
          waste_category_code: wasteCategory.wasteCategoryCode,
          waste_category_density: wasteCategory.wasteCategoryDensity,
          waste_category_authorized_materials:
            wasteCategory.wasteCategoryAuthorizedMaterial,
          waste_category_mongo_id: wasteCategory.id,
        };
      }

      const blobNames = getOutputSurfaceBlobNames(
        history.image_url,
        history._time,
        history._measurement,
      );

      const remapAuthorized = (segmentations) =>
        (segmentations || []).map((item) => ({
          ...item,
          isAuthorizedMaterial: wasteCategory
            ? (wasteCategory.wasteCategoryAuthorizedMaterial || []).some(
                (material) => material.value === item.cls,
              )
            : item.isAuthorizedMaterial ?? false,
        }));

      let updatedAtLeastOne = false;
      for (const blobName of blobNames) {
        try {
          const { data: outputSurface } = await getAzureBlob({
            blobUrl: azureUrl + blobName,
          });
          objectSegmentations = remapAuthorized(
            outputSurface.object_segmentations,
          );
          const params = buildParams(
            { ...outputSurface, ...baseParams },
            objectSegmentations,
          );
          await updateOutputSurface({
            data: params,
            blobName,
            toCharacterization: characterization,
            captureTime: history?._time,
          });
          updatedAtLeastOne = true;
          updatedCount += 1;
        } catch (error) {
          if (error.response?.status !== 404) {
            console.error("Erreur applyWasteCategoryToCycleSurfaces:", error);
          }
        }
      }

      if (!updatedAtLeastOne && history?.anomaliesAnnotation) {
        objectSegmentations = remapAuthorized(history.anomaliesAnnotation);
        const params = buildParams(baseParams, objectSegmentations);
        for (const blobName of blobNames) {
          try {
            await updateOutputSurface({
              data: params,
              blobName,
              toCharacterization: characterization,
              captureTime: history?._time,
            });
            updatedCount += 1;
            updatedAtLeastOne = true;
          } catch (error) {
            console.error(
              "Erreur applyWasteCategoryToCycleSurfaces fallback:",
              error,
            );
          }
        }
      }
    }),
  );

  return { updatedCount };
}

/**
 * Resolve history cycle entries for a flux change from Redux collection data,
 * falling back to the local historyCycle when Redux is empty.
 */
export function resolveHistoryEntriesForFluxChange({
  superContainerTimeLine,
  timeLine,
  collectionData,
  historyCycleFallback,
}) {
  let collections = [];

  if (superContainerTimeLine) {
    const leftSide = superContainerTimeLine.find((i) =>
      i._value_image?.includes("-L.jpeg"),
    );
    const collection =
      collectionData?.[`${leftSide?._measurement}_${leftSide?._time}`] || [];
    collections = Array.isArray(collection) ? [...collection] : [];
  } else if (timeLine) {
    const collection =
      collectionData?.[`${timeLine._measurement}_${timeLine._time}`];
    collections = collection ? [collection] : [];
  }

  let historyEntries = collections.flatMap(
    (collection) => collection?.historyCurrent || [],
  );

  if (
    historyEntries.length === 0 &&
    Array.isArray(historyCycleFallback) &&
    historyCycleFallback.length > 0
  ) {
    historyEntries = historyCycleFallback;
  }

  return historyEntries;
}
